import React, { type FC, useMemo } from 'react'
import { Security } from '@mui/icons-material'

import { copyObjectWithoutKeys, isArray } from '../../../../utils'

import { type ConnectwareError, type CybusRole, Translation } from '../../../../domain'

import { Autocomplete, type WithIcon, type WithMeta, type WithRequired } from '../../common'
import { FormattedTranslation } from '../../Internationalization'

type Props = Readonly<{
    disabled: boolean
    roles: readonly CybusRole[]
    required?: readonly CybusRole['name'][]
    allRoles: readonly CybusRole[] | ConnectwareError | null
    usecase: { searchRoles(search: string | null): void, selectRoles(roles: readonly CybusRole[]): void }
}>

/** Shared, so the default keeps its identity between renders: a new array would change `value` and clear the typed search */
const NO_REQUIRED_ROLES: readonly CybusRole['name'][] = []

const withOptionContent = (role: CybusRole): WithMeta<WithIcon<CybusRole>> => ({
    ...role,
    icon: <Security />,
    meta: <FormattedTranslation id={Translation.TOTAL_PERMISSIONS} values={{ count: role.permissions.length }} />,
})

export const RolesDropdown: FC<Props> = ({ disabled, roles, required = NO_REQUIRED_ROLES, allRoles, usecase }) => {
    const value = useMemo<readonly WithRequired<CybusRole>[]>(
        () => roles.map((role) => ({ ...role, required: required.includes(role.name) })),
        [roles, required]
    )
    const options = useMemo(() => (isArray(allRoles) ? allRoles.map(withOptionContent) : allRoles), [allRoles])

    return (
        <Autocomplete
            data-testid="roles"
            multiple
            options={options}
            getOptionLabel={(option) => option.name}
            onSearch={(term) => usecase.searchRoles(term)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText={Translation.NO_ROLES_FOUND}
            value={value}
            disabled={disabled}
            onOpen={() => usecase.searchRoles('')}
            onClose={() => usecase.searchRoles(null)}
            /** Strips the render-only `required`/`icon`/`meta` fields back off before it reaches app state, which should only ever hold real `CybusRole`s */
            onSelect={(roles) => usecase.selectRoles(roles.map((role) => copyObjectWithoutKeys(role, 'required', 'icon', 'meta')))}
            // If only the required items are selected, than they auto clearing is not available
            disableClearable={required.length >= roles.length}
        />
    )
}
